import React, { useState, useEffect, useCallback, memo } from 'react';

export const SectionDialog = memo(({
  isOpen,
  onClose,
  onSave,
  editingIndex,
  initialData,
  allFeatures,
  selectedVenueTypeSubCategoryId,
}) => {
  const [sectionName, setSectionName] = useState('');
  const [sectionArea, setSectionArea] = useState('');
  const [sectionMinCapacity, setSectionMinCapacity] = useState('');
  const [sectionMaxCapacity, setSectionMaxCapacity] = useState('');
  const [sectionPricePerHour, setSectionPricePerHour] = useState('');
  const [sectionOfferedPrice, setSectionOfferedPrice] = useState('');
  const [tempSelectedFeatureIds, setTempSelectedFeatureIds] = useState([]);

  // Reset form when dialog opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData && editingIndex !== null) {
        setSectionName(initialData.name || '');
        setSectionArea(initialData.areaSqft?.toString() || '');
        setSectionMinCapacity(initialData.minCapacity?.toString() || '');
        setSectionMaxCapacity(initialData.maxCapacity?.toString() || '');
        setSectionPricePerHour(initialData.pricePerHour?.toString() || '');
        setSectionOfferedPrice(initialData.offeredPrice?.toString() || '');
        setTempSelectedFeatureIds(initialData.featureIds || []);
      } else {
        setSectionName('');
        setSectionArea('');
        setSectionMinCapacity('');
        setSectionMaxCapacity('');
        setSectionPricePerHour('');
        setSectionOfferedPrice('');
        setTempSelectedFeatureIds([]);
      }
    }
  }, [isOpen, initialData, editingIndex]);

  const toggleFeature = useCallback((featureId) => {
    setTempSelectedFeatureIds(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  }, []);

  const handleSave = useCallback(() => {
    const name = sectionName.trim();
    const area = parseInt(sectionArea);
    const minCap = parseInt(sectionMinCapacity);
    const maxCap = parseInt(sectionMaxCapacity);
    const price = parseFloat(sectionPricePerHour);
    const offeredPrice = sectionOfferedPrice ? parseFloat(sectionOfferedPrice) : null;

    if (!name || isNaN(area) || isNaN(minCap) || isNaN(maxCap) || isNaN(price)) {
      alert("Please fill all required fields");
      return;
    }

    if (minCap > maxCap) {
      alert("Min capacity cannot be greater than max capacity");
      return;
    }

    onSave({
      name,
      venueTypeId: selectedVenueTypeSubCategoryId || 0,
      areaSqft: area,
      minCapacity: minCap,
      maxCapacity: maxCap,
      pricePerHour: price,
      offeredPrice: offeredPrice,
      featureIds: tempSelectedFeatureIds,
      images: []
    });
  }, [sectionName, sectionArea, sectionMinCapacity, sectionMaxCapacity, 
      sectionPricePerHour, sectionOfferedPrice, tempSelectedFeatureIds, 
      selectedVenueTypeSubCategoryId, onSave]);

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container section-dialog" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <span className="dialog-icon">{editingIndex !== null ? '✏️' : '📍'}</span>
          <span className="dialog-title">{editingIndex !== null ? "Edit Space" : "Add New Space"}</span>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="dialog-body">
          <div className="dialog-field">
            <label>Space Name</label>
            <input
              type="text"
              placeholder="e.g., Main Hall, Garden Area"
              value={sectionName}
              onChange={e => setSectionName(e.target.value)}
            />
          </div>
          <div className="dialog-row">
            <div className="dialog-field">
              <label>Area (sq ft)</label>
              <input
                type="number"
                placeholder="Area"
                value={sectionArea}
                onChange={e => setSectionArea(e.target.value)}
              />
            </div>
            <div className="dialog-field">
              <label>Min Capacity</label>
              <input
                type="number"
                placeholder="Min"
                value={sectionMinCapacity}
                onChange={e => setSectionMinCapacity(e.target.value)}
              />
            </div>
            <div className="dialog-field">
              <label>Max Capacity</label>
              <input
                type="number"
                placeholder="Max"
                value={sectionMaxCapacity}
                onChange={e => setSectionMaxCapacity(e.target.value)}
              />
            </div>
          </div>
          <div className="dialog-row">
            <div className="dialog-field">
              <label>Price (₹/hr)</label>
              <input
                type="number"
                placeholder="Regular price"
                value={sectionPricePerHour}
                onChange={e => setSectionPricePerHour(e.target.value)}
              />
            </div>
            <div className="dialog-field">
              <label>Offer Price</label>
              <input
                type="number"
                placeholder="Optional"
                value={sectionOfferedPrice}
                onChange={e => setSectionOfferedPrice(e.target.value)}
              />
            </div>
          </div>
          {allFeatures.length > 0 && (
            <div className="dialog-features">
              <div className="features-header">
                <span>✓ Features</span>
                <span className="feature-count">{tempSelectedFeatureIds.length} selected</span>
              </div>
              <div className="feature-chips-select">
                {allFeatures.map(feature => (
                  <button
                    key={feature.id}
                    className={`feature-select-chip ${tempSelectedFeatureIds.includes(feature.id) ? 'selected' : ''}`}
                    onClick={() => toggleFeature(feature.id)}
                  >
                    {feature.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="dialog-footer">
          <button className="dialog-cancel" onClick={onClose}>Cancel</button>
          <button className="dialog-save" onClick={handleSave}>{editingIndex !== null ? "Update" : "Add"}</button>
        </div>
      </div>
    </div>
  );
});

SectionDialog.displayName = 'SectionDialog';